// One open filter picker at a time, PAGE-WIDE — the legacy's module-level singletons
// (04m-b fork 2B). The legacy keeps one open value picker + one open range picker per kit,
// as module-level variables, so opening a picker anywhere on the page closes the one
// already open — in the same widget or another. Our React bars used to track their own
// picker each, so two could be open at once (in two widgets, or Zone Finder's "What" chip
// next to its "How" bar).
//
// Two channels, like the legacy's two kits (they never close each other):
// - "event": EventMapKit's pickers (renderers-shotmap.js) — EventFilterBar, Colour-by, Zone
//   Finder's What chip. Opening a VALUE picker closes only the open value picker; opening a
//   RANGE picker closes both (openEventValuePicker / openEventRangePicker). That asymmetry
//   is the legacy's, kept.
// - "stats": StatsFilterKit's pickers (renderers-stats.js) — FilterChipBar. Opening either
//   kind closes both.

import { useCallback, useEffect, useState } from "react";

export type PickerChannel = "event" | "stats";
export type PickerKind = "value" | "range";

/** The picker kinds an opening picker closes first (the legacy rules above). */
export function closedByOpening(channel: PickerChannel, kind: PickerKind): PickerKind[] {
  return channel === "event" && kind === "value" ? ["value"] : ["value", "range"];
}

/** The slot bookkeeping, pure (no React) so it's unit-tested. A "closer" is the close
 *  function of whichever component has that slot's picker open. */
export function createPickerSlots() {
  const openClosers = new Map<string, () => void>();
  const slotOf = (channel: PickerChannel, kind: PickerKind) => `${channel}:${kind}`;
  return {
    /** Close what the rules say, then hold the slot. Our own open picker isn't closed —
     *  the new state just replaces it. */
    claim(channel: PickerChannel, kind: PickerKind, closer: () => void): void {
      for (const k of closedByOpening(channel, kind)) {
        const other = slotOf(channel, k);
        const close = openClosers.get(other);
        if (close && close !== closer) {
          openClosers.delete(other);
          close();
        }
      }
      openClosers.set(slotOf(channel, kind), closer);
    },
    /** Give the slot up (closed by itself, or unmounted) — only if we still hold it. */
    release(channel: PickerChannel, kind: PickerKind, closer: () => void): void {
      const slot = slotOf(channel, kind);
      if (openClosers.get(slot) === closer) openClosers.delete(slot);
    },
    /** Close every open picker in a channel. */
    closeAll(channel: PickerChannel): void {
      for (const kind of ["value", "range"] as const) {
        const slot = slotOf(channel, kind);
        const close = openClosers.get(slot);
        openClosers.delete(slot);
        close?.();
      }
    },
  };
}

const slots = createPickerSlots();

/** Close every open picker in a channel — the legacy's chip ✕ closes both kinds, page-wide. */
export function closeAllPickers(channel: PickerChannel): void {
  slots.closeAll(channel);
}

/**
 * A component's picker state that takes part in the page-wide slot. Returns the open
 * picker's state (null = closed), `open` (closes whatever the rules say first) and `close`.
 */
export function useSlotPicker<T>(channel: PickerChannel, kind: PickerKind): [T | null, (next: T) => void, () => void] {
  const [state, setState] = useState<T | null>(null);
  // One stable closer per component + kind — how another opener closes this one.
  const [closer] = useState(() => () => setState(null));

  const open = useCallback(
    (next: T) => {
      slots.claim(channel, kind, closer);
      setState(next);
    },
    [channel, kind, closer],
  );
  const close = useCallback(() => {
    slots.release(channel, kind, closer);
    setState(null);
  }, [channel, kind, closer]);

  // An unmount mid-pick must not leave a dead closer holding the slot.
  useEffect(() => () => slots.release(channel, kind, closer), [channel, kind, closer]);

  return [state, open, close];
}
